import { useState, useEffect, useRef } from 'react'
import { aiApi, AIBatchAnalyzeRequest, TableColumnTemplate, AnalysisResponse } from '../api/ai'
import { datasetApi } from '../api/datasets'
import { useSession } from '../contexts/SessionContext'
import ReactMarkdown from 'react-markdown'
import AnalysisJobsPanel from '../components/AnalysisJobsPanel'
import BookmarkStar from '../components/BookmarkStar'

// Turn bare PMCID / synthetic PMID_ tokens in the AI text into markdown links so
// they render as hyperlinks. The lookarounds avoid touching tokens already part
// of a URL or another word. The custom `a` renderer below then appends a ★.
function linkifyIds(text: string): string {
  return text
    .replace(/(?<![\/\w])(PMC\d+)(?![\/\w])/g,
      (_m, id) => `[${id}](https://www.ncbi.nlm.nih.gov/pmc/articles/${id})`)
    .replace(/(?<![\/\w])PMID_(\d+)(?![\/\w])/g,
      (_m, num) => `[PMID:${num}](https://pubmed.ncbi.nlm.nih.gov/${num}/)`)
}

// Recover the bookmark key (PMCID or synthetic PMID_<id>) from an NCBI href.
function pmcidFromHref(href?: string): string | null {
  if (!href) return null
  let m = href.match(/pmc\/articles\/(PMC\d+)/)
  if (m) return m[1]
  m = href.match(/pubmed\.ncbi\.nlm\.nih\.gov\/(\d+)/)
  if (m) return `PMID_${m[1]}`
  return null
}

// ReactMarkdown link renderer: render the hyperlink, and for NCBI article links
// append an inline bookmark star.
const mdComponents = {
  a: ({ href, children }: any) => {
    const pmcid = pmcidFromHref(href)
    return (
      <span className="inline-flex items-center gap-0.5 whitespace-nowrap">
        <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{children}</a>
        {pmcid && <BookmarkStar pmcid={pmcid} />}
      </span>
    )
  },
}

interface AnalysisImage {
  image_id: string
  analysis_id: string
  image_base64: string
  image_mime: string
  prompt?: string
  is_primary: boolean
  created_at: string
}

const DEFAULT_COLUMNS: TableColumnTemplate[] = [
  { name: 'PMCID', instruction: 'The PMCID of the article' },
  { name: 'Key Finding', instruction: 'Summarize the main finding relevant to the question' },
]

export default function AIAnalysisPage() {
  const { activeSessionId } = useSession()
  const [datasetId, setDatasetId] = useState('')
  const [datasets, setDatasets] = useState<{ dataset_id: string; name: string; row_count: number }[]>([])

  const [question, setQuestion] = useState('')
  const [outputFormat, setOutputFormat] = useState<'text' | 'table'>('text')
  const [detailLevel, setDetailLevel] = useState<'normal' | 'detailed'>('normal')
  const [temperature, setTemperature] = useState(0.2)
  const [maxRows, setMaxRows] = useState(400)
  const [maxRowsCap, setMaxRowsCap] = useState(1000)  // admin-configured ceiling
  const [rowSelection, setRowSelection] = useState<'top' | 'random'>('top')

  // Table template builder
  const [columns, setColumns] = useState<TableColumnTemplate[]>([...DEFAULT_COLUMNS])

  // Image generation
  const [isGeneratingImage, setIsGeneratingImage] = useState(false)
  const [refinePrompt, setRefinePrompt] = useState('')
  const [isRefiningImage, setIsRefiningImage] = useState(false)
  const [analysisImages, setAnalysisImages] = useState<AnalysisImage[]>([])
  const [displayedImageId, setDisplayedImageId] = useState<string | null>(null)
  const [imageCost, setImageCost] = useState<number | null>(null)

  const [isRunning, setIsRunning] = useState(false)
  const [result, setResult] = useState<any>(null)
  const [errorMsg, setErrorMsg] = useState('')
  const [currentAnalysisId, setCurrentAnalysisId] = useState<string | null>(null)
  const [loadedJobName, setLoadedJobName] = useState('')

  // Table output sorting
  const [sortCol, setSortCol] = useState<string | null>(null)
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')

  const pollInterval = useRef<ReturnType<typeof setInterval> | null>(null)

  // Datasets and the restored job are scoped to the active session. Switching
  // sessions reloads the dataset list, clears any selection/result that doesn't
  // belong, and only restores a saved job if it lives in this session.
  // Load the admin-configured max-rows ceiling once, and clamp the slider to it.
  useEffect(() => {
    aiApi.getConfig()
      .then(r => {
        const cap = r.data.max_rows || 1000
        setMaxRowsCap(cap)
        setMaxRows(prev => Math.min(prev, cap))
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (!activeSessionId) {
      setDatasets([])
      setDatasetId('')
      setCurrentAnalysisId(null)
      setResult(null)
      setLoadedJobName('')
      return
    }
    datasetApi.list(activeSessionId).then(r => {
      setDatasets(r.data)
      setDatasetId(prev => (r.data.some(d => d.dataset_id === prev) ? prev : ''))
    }).catch(() => setDatasets([]))

    const savedId = localStorage.getItem('pmc_ev_last_analysis_id')
    if (savedId) {
      loadJobById(savedId)
    } else {
      setCurrentAnalysisId(null)
      setResult(null)
      setLoadedJobName('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSessionId])

  useEffect(() => {
    return () => {
      if (pollInterval.current) clearInterval(pollInterval.current)
    }
  }, [])

  const loadJobById = async (analysisId: string) => {
    try {
      const res = await aiApi.getAnalysis(analysisId)
      // Don't resurrect a job that belongs to a different session.
      if (activeSessionId && res.data.session_id && res.data.session_id !== activeSessionId) {
        localStorage.removeItem('pmc_ev_last_analysis_id')
        setCurrentAnalysisId(null)
        setResult(null)
        setLoadedJobName('')
        return
      }
      if (res.data.status === 'complete') {
        setCurrentAnalysisId(analysisId)
        setResult(res.data.results)
        setLoadedJobName(res.data.name)
        if (res.data.images && res.data.images.length > 0) {
          setAnalysisImages(res.data.images)
          const primary = res.data.images.find((img: AnalysisImage) => img.is_primary)
          setDisplayedImageId(primary ? primary.image_id : res.data.images[0].image_id)
        } else {
          setAnalysisImages([])
          setDisplayedImageId(null)
        }
        // Restore settings from params
        const p = res.data.params || {}
        setOutputFormat(p.output_format || 'text')
        setDetailLevel(p.detail_level || 'normal')
        setTemperature(p.temperature ?? 0.2)
        setMaxRows(p.max_rows ?? 400)
        setRowSelection(p.row_selection || 'top')
        if (p.table_template) setColumns(p.table_template)
      } else if (res.data.status === 'running' || res.data.status === 'pending') {
        setCurrentAnalysisId(analysisId)
        setLoadedJobName(res.data.name)
        startPolling(analysisId)
      }
    } catch {
      localStorage.removeItem('pmc_ev_last_analysis_id')
    }
  }

  const handleLoadJob = (job: AnalysisResponse) => {
    setCurrentAnalysisId(job.analysis_id)
    setResult(job.results)
    setLoadedJobName(job.name)
    setErrorMsg('')
    if (job.images && job.images.length > 0) {
      setAnalysisImages(job.images)
      const primary = job.images.find((img: AnalysisImage) => img.is_primary)
      setDisplayedImageId(primary ? primary.image_id : job.images[0].image_id)
    } else {
      setAnalysisImages([])
      setDisplayedImageId(null)
    }
    // Restore settings
    const p = job.params || {}
    setOutputFormat(p.output_format || 'text')
    setDetailLevel(p.detail_level || 'normal')
    setTemperature(p.temperature ?? 0.2)
    setMaxRows(p.max_rows ?? 400)
    setRowSelection(p.row_selection || 'top')
    if (p.table_template) setColumns(p.table_template)
    localStorage.setItem('pmc_ev_last_analysis_id', job.analysis_id)
  }

  const startAnalysis = async () => {
    setErrorMsg('')
    setResult(null)
    setAnalysisImages([])
    setDisplayedImageId(null)
    setRefinePrompt('')
    setImageCost(null)
    setLoadedJobName('')

    if (!datasetId) {
      setErrorMsg('Please select a dataset. Save your search results as a dataset first.')
      return
    }

    if (!question.trim() && outputFormat === 'text') {
      setErrorMsg('Please enter a question')
      return
    }

    let payload: AIBatchAnalyzeRequest = {
      dataset_id: datasetId,
      question: question.trim(),
      output_format: outputFormat,
      detail_level: detailLevel,
      temperature,
      max_rows: maxRows,
      row_selection: rowSelection,
    }

    if (outputFormat === 'table' && columns.length > 0) {
      payload.table_template = columns
    }

    setIsRunning(true)
    try {
      const res = await aiApi.batchAnalyze(payload)
      setCurrentAnalysisId(res.data.analysis_id)
      localStorage.setItem('pmc_ev_last_analysis_id', res.data.analysis_id)
      startPolling(res.data.analysis_id)
    } catch (e: any) {
      const msg = e.response?.data?.detail || e.response?.data?.error || 'Analysis failed to start'
      setErrorMsg(msg)
      setIsRunning(false)
    }
  }

  const startPolling = (analysisId: string) => {
    if (pollInterval.current) clearInterval(pollInterval.current)
    pollInterval.current = setInterval(async () => {
      try {
        const res = await aiApi.getAnalysis(analysisId)
        if (res.data.status === 'complete') {
          clearInterval(pollInterval.current!)
          setIsRunning(false)
          setResult(res.data.results)
          setLoadedJobName(res.data.name)
          if (res.data.images && res.data.images.length > 0) {
            setAnalysisImages(res.data.images)
            const primary = res.data.images.find((img: AnalysisImage) => img.is_primary)
            setDisplayedImageId(primary ? primary.image_id : res.data.images[0].image_id)
          }
        } else if (res.data.status === 'error') {
          clearInterval(pollInterval.current!)
          setIsRunning(false)
          setErrorMsg(res.data.error_message || 'Analysis failed')
        } else if (res.data.status === 'cancelled') {
          clearInterval(pollInterval.current!)
          setIsRunning(false)
          setErrorMsg('Analysis was cancelled')
        }
      } catch (e) {
        clearInterval(pollInterval.current!)
        setIsRunning(false)
      }
    }, 3000)
  }

  const addColumn = () => {
    setColumns([...columns, { name: '', instruction: '' }])
  }

  const removeColumn = (idx: number) => {
    if (idx === 0) return
    setColumns(columns.filter((_, i) => i !== idx))
  }

  const updateColumn = (idx: number, field: keyof TableColumnTemplate, value: string) => {
    const next = [...columns]
    next[idx] = { ...next[idx], [field]: value }
    setColumns(next)
  }

  const generateDiagram = async () => {
    if (!currentAnalysisId) return
    setIsGeneratingImage(true)
    setErrorMsg('')
    try {
      const res = await aiApi.generateImage(currentAnalysisId)
      const newImg: AnalysisImage = {
        image_id: res.data.image_id,
        analysis_id: currentAnalysisId,
        image_base64: res.data.image_base64,
        image_mime: res.data.image_mime,
        prompt: res.data.prompt,
        is_primary: res.data.is_primary,
        created_at: res.data.created_at,
      }
      setAnalysisImages([newImg])
      setDisplayedImageId(newImg.image_id)
      setImageCost(res.data.cost)
      setResult((prev: any) => prev ? {
        ...prev,
        image_base64: res.data.image_base64,
        image_mime: res.data.image_mime,
      } : prev)
    } catch (e: any) {
      setErrorMsg(e.response?.data?.detail || 'Image generation failed')
    } finally {
      setIsGeneratingImage(false)
    }
  }

  const refineImage = async () => {
    if (!refinePrompt.trim() || !currentAnalysisId) return
    setIsRefiningImage(true)
    setErrorMsg('')
    try {
      const res = await aiApi.refineImage(currentAnalysisId, refinePrompt.trim())
      const newImg: AnalysisImage = {
        image_id: res.data.image_id,
        analysis_id: currentAnalysisId,
        image_base64: res.data.image_base64,
        image_mime: res.data.image_mime,
        prompt: res.data.prompt,
        is_primary: res.data.is_primary,
        created_at: res.data.created_at,
      }
      setAnalysisImages(prev => [...prev.map(img => ({ ...img, is_primary: false })), newImg])
      setDisplayedImageId(newImg.image_id)
      setImageCost(res.data.cost)
      setResult((prev: any) => prev ? {
        ...prev,
        image_base64: res.data.image_base64,
        image_mime: res.data.image_mime,
      } : prev)
      setRefinePrompt('')
    } catch (e: any) {
      setErrorMsg(e.response?.data?.detail || 'Image refinement failed')
    } finally {
      setIsRefiningImage(false)
    }
  }

  const downloadImage = (base64: string, mime: string) => {
    const link = document.createElement('a')
    link.href = `data:${mime};base64,${base64}`
    link.download = `scientific_diagram_${new Date().toISOString().slice(0, 10)}.png`
    link.click()
  }

  const selectedDataset = datasets.find(d => d.dataset_id === datasetId)

  const hasTableTemplate = outputFormat === 'table' && columns.length > 1 && columns.every(c => c.name.trim() && c.instruction.trim())
  const canStart = datasetId && (question.trim() || hasTableTemplate) && !isRunning

  const displayedImage = analysisImages.find(img => img.image_id === displayedImageId)
    || analysisImages.find(img => img.is_primary)
    || analysisImages[0]
    || (result?.image_base64 ? {
        image_id: 'legacy',
        analysis_id: '',
        image_base64: result.image_base64,
        image_mime: result.image_mime || 'image/png',
        is_primary: true,
        created_at: '',
      } as AnalysisImage : null)

  const hasTextResults = outputFormat === 'text' && result?.text
  const hasImages = analysisImages.length > 0 || result?.image_base64

  // Table output: sorting + CSV export
  const tableRows: any[] = Array.isArray(result?.table_data) ? result.table_data : []
  const tableCols = tableRows.length > 0 ? Object.keys(tableRows[0]) : []
  const sortedTableRows = sortCol
    ? [...tableRows].sort((a, b) => {
        const av = a[sortCol], bv = b[sortCol]
        if (av == null && bv == null) return 0
        if (av == null) return 1
        if (bv == null) return -1
        const cmp = typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv), undefined, { numeric: true })
        return sortDir === 'asc' ? cmp : -cmp
      })
    : tableRows
  const toggleTableSort = (c: string) => {
    if (sortCol === c) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))
    else { setSortCol(c); setSortDir('asc') }
  }
  const tableSortInd = (c: string) => (sortCol === c ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '')

  const exportTableCsv = () => {
    if (sortedTableRows.length === 0) return
    const esc = (v: any) => {
      const s = v == null ? '' : String(v)
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
    }
    const lines = [tableCols.map(esc).join(',')]
    for (const row of sortedTableRows) lines.push(tableCols.map(c => esc(row[c])).join(','))
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `ai_analysis_${loadedJobName || 'table'}_${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div>
    {!activeSessionId && (
      <div className="mb-4 p-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg text-sm">
        No session selected. Datasets and AI jobs are scoped to a session — pick or create one from
        the <strong>Session</strong> picker in the top bar to see your datasets and run analyses.
      </div>
    )}
    <div className="flex gap-6">
      {/* Left Sidebar — Settings & Jobs */}
      <div className="w-80 shrink-0 space-y-4">
        {/* Data Source — Dataset Only */}
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Dataset</h3>
          <p className="text-xs text-gray-500 mb-2">
            AI analysis requires a saved dataset.
          </p>
          <select
            value={datasetId}
            onChange={e => setDatasetId(e.target.value)}
            className="w-full px-2 py-1 border rounded text-sm"
          >
            <option value="">Select a saved dataset...</option>
            {datasets.map(d => (
              <option key={d.dataset_id} value={d.dataset_id}>{d.name} ({d.row_count} rows)</option>
            ))}
          </select>
          {selectedDataset && (
            <div className="text-xs text-gray-500 bg-gray-50 rounded px-2 py-1 mt-2">
              <strong>{selectedDataset.row_count?.toLocaleString()}</strong> rows
            </div>
          )}
          {datasets.length === 0 && (
            <div className="text-xs text-yellow-600 bg-yellow-50 rounded px-2 py-1 mt-2">
              {activeSessionId
                ? 'No datasets in this session. Run a search, then save results as a dataset.'
                : 'Select a session (top bar) to see its datasets.'}
            </div>
          )}
        </div>

        {/* Output Settings */}
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Output Settings</h3>

          <div className="mb-3">
            <label className="block text-xs text-gray-500 mb-1">Format</label>
            <select
              value={outputFormat}
              onChange={e => setOutputFormat(e.target.value as any)}
              className="w-full px-2 py-1 border rounded text-sm"
            >
              <option value="text">Text</option>
              <option value="table">Table</option>
            </select>
          </div>

          <div className="mb-3">
            <label className="block text-xs text-gray-500 mb-1">Detail Level</label>
            <select
              value={detailLevel}
              onChange={e => setDetailLevel(e.target.value as any)}
              className="w-full px-2 py-1 border rounded text-sm"
            >
              <option value="normal">Normal</option>
              <option value="detailed">Detailed</option>
            </select>
          </div>

          <div className="mb-3">
            <label className="block text-xs text-gray-500 mb-1">Max Rows ({maxRows}) <span className="text-gray-400">· max {maxRowsCap}</span></label>
            <input
              type="range"
              min={50}
              max={maxRowsCap}
              step={50}
              value={maxRows}
              onChange={e => setMaxRows(Number(e.target.value))}
              className="w-full"
            />
          </div>

          <div className="mb-3">
            <label className="block text-xs text-gray-500 mb-1">Row Selection</label>
            <select
              value={rowSelection}
              onChange={e => setRowSelection(e.target.value as any)}
              className="w-full px-2 py-1 border rounded text-sm"
            >
              <option value="top">Top</option>
              <option value="random">Random</option>
            </select>
          </div>

          <div className="mb-3">
            <label className="block text-xs text-gray-500 mb-1">Temperature ({temperature})</label>
            <input
              type="range"
              min={0}
              max={2}
              step={0.1}
              value={temperature}
              onChange={e => setTemperature(Number(e.target.value))}
              className="w-full"
            />
          </div>
        </div>

        {/* Table Template Builder */}
        {outputFormat === 'table' && (
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Table Template</h3>
            <p className="text-xs text-gray-500 mb-3">
              Define columns for the output table. PMCID is always included.
            </p>
            <div className="space-y-2 mb-3">
              {columns.map((col, idx) => (
                <div key={idx} className="bg-gray-50 rounded p-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-gray-500">Column {idx + 1}</span>
                    {idx > 0 && (
                      <button onClick={() => removeColumn(idx)} className="text-xs text-red-500 hover:underline">
                        Remove
                      </button>
                    )}
                  </div>
                  <input
                    type="text"
                    value={col.name}
                    onChange={e => updateColumn(idx, 'name', e.target.value)}
                    placeholder="Column name"
                    disabled={idx === 0}
                    className="w-full px-2 py-1 border rounded text-sm mb-1 disabled:bg-gray-100"
                  />
                  <textarea
                    value={col.instruction}
                    onChange={e => updateColumn(idx, 'instruction', e.target.value)}
                    placeholder="What information should this column contain?"
                    rows={2}
                    className="w-full px-2 py-1 border rounded text-sm"
                  />
                </div>
              ))}
            </div>
            <button
              onClick={addColumn}
              className="w-full py-1.5 border-2 border-dashed border-gray-300 rounded text-sm text-gray-500 hover:border-gray-400 hover:text-gray-600"
            >
              + Add Column
            </button>
          </div>
        )}

        {/* Job History Panel */}
        <AnalysisJobsPanel
          onLoadJob={handleLoadJob}
          activeAnalysisId={currentAnalysisId}
          activeSessionId={activeSessionId}
        />
      </div>

      {/* Right Panel — Question & Results */}
      <div className="flex-1 min-w-0 space-y-4">
        {/* Question & Run */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h2 className="text-lg font-semibold mb-4">AI Analysis</h2>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {outputFormat === 'table' && hasTableTemplate
                ? 'Question (optional when table template is set)'
                : 'Question'}
            </label>
            <textarea
              value={question}
              onChange={e => setQuestion(e.target.value)}
              rows={5}
              className="w-full px-3 py-2 border rounded-lg text-sm"
              placeholder={
                outputFormat === 'table'
                  ? 'Optional: add context for the table extraction...'
                  : 'Ask a question about the data...'
              }
            />
          </div>

          <button
            onClick={startAnalysis}
            disabled={!canStart}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {isRunning ? 'Analyzing...' : 'Run Analysis'}
          </button>

          {errorMsg && <div className="mt-4 text-red-600 text-sm">{errorMsg}</div>}
        </div>

        {/* Loading */}
        {isRunning && (
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex items-center gap-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
              <span className="text-gray-600">Analysis in progress... This may take a few minutes.</span>
            </div>
          </div>
        )}

        {/* Results */}
        {result && (
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
              <h2 className="text-lg font-semibold">Results</h2>
              <div className="flex items-center gap-2">
                {loadedJobName && (
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    {loadedJobName}
                  </span>
                )}
                {outputFormat === 'table' && tableRows.length > 0 && (
                  <button
                    onClick={exportTableCsv}
                    className="px-3 py-1.5 bg-green-600 text-white rounded text-sm font-medium hover:bg-green-700"
                  >
                    Export CSV
                  </button>
                )}
              </div>
            </div>

            {outputFormat === 'table' && tableRows.length > 0 && (
              <div className="overflow-x-auto mb-4">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      {tableCols.map(key => (
                        <th key={key} className="px-4 py-2 text-left font-medium text-gray-700">
                          <button onClick={() => toggleTableSort(key)} className="hover:text-gray-900">{key}{tableSortInd(key)}</button>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {sortedTableRows.map((row: any, idx: number) => (
                      <tr key={idx} className="hover:bg-gray-50 align-top">
                        {Object.entries(row).map(([key, val]: [string, any], i: number) => (
                          <td key={i} className="px-4 py-2 break-words">
                            {key.toLowerCase().includes('pmcid') && typeof val === 'string' && val
                              ? <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                                  <BookmarkStar pmcid={val} title={String((row as any).Title || (row as any).title || '')} />
                                  <a href={val.startsWith('PMID_') ? `https://pubmed.ncbi.nlm.nih.gov/${val.replace('PMID_', '')}/` : `https://www.ncbi.nlm.nih.gov/pmc/articles/${val}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-mono">{val.startsWith('PMID_') ? `PMID:${val.replace('PMID_', '')}` : val}</a>
                                </span>
                              : String(val ?? '')
                            }
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="mt-2 text-xs text-gray-500">
                  {result.table_data.length} rows
                </div>
              </div>
            )}

            {outputFormat === 'text' && result.text && (
              <div className="prose prose-sm max-w-none mb-4">
                <ReactMarkdown components={mdComponents}>{linkifyIds(result.text)}</ReactMarkdown>
              </div>
            )}

            {/* Generate Diagram button — appears after text output */}
            {hasTextResults && !hasImages && (
              <div className="mt-6 p-4 border-2 border-dashed border-purple-200 rounded-xl bg-purple-50">
                <p className="text-sm text-purple-800 mb-3">
                  Generate a scientific diagram from these findings.
                </p>
                <button
                  onClick={generateDiagram}
                  disabled={isGeneratingImage}
                  className="px-5 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50"
                >
                  {isGeneratingImage ? 'Generating...' : 'Generate Diagram'}
                </button>
              </div>
            )}

            {/* Generated Images Section */}
            {displayedImage && (
              <div className="mt-6 p-4 border-2 border-blue-200 rounded-xl bg-blue-50">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-md font-semibold text-blue-800">Scientific Diagram</h3>
                  {imageCost !== null && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                      {imageCost} AI units used
                    </span>
                  )}
                </div>

                <img
                  src={`data:${displayedImage.image_mime};base64,${displayedImage.image_base64}`}
                  alt="Scientific diagram"
                  className="max-w-full rounded-lg shadow"
                />

                {analysisImages.length > 1 && (
                  <div className="mt-4">
                    <p className="text-xs font-medium text-blue-700 mb-2">All versions:</p>
                    <div className="flex gap-2 flex-wrap">
                      {analysisImages.map((img, idx) => (
                        <button
                          key={img.image_id}
                          onClick={() => setDisplayedImageId(img.image_id)}
                          className={`relative w-16 h-16 rounded border-2 overflow-hidden ${
                            displayedImageId === img.image_id || (displayedImageId === null && img.is_primary)
                              ? 'border-blue-600 ring-2 ring-blue-200'
                              : 'border-gray-300 hover:border-gray-400'
                          }`}
                          title={img.prompt || `Version ${idx + 1}`}
                        >
                          <img
                            src={`data:${img.image_mime};base64,${img.image_base64}`}
                            alt={`Version ${idx + 1}`}
                            className="w-full h-full object-cover"
                          />
                          <span className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[10px] text-center">
                            {idx + 1}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-4 pt-4 border-t border-blue-200">
                  <label className="block text-xs font-medium text-blue-700 mb-1">
                    Modify this diagram
                  </label>
                  <textarea
                    value={refinePrompt}
                    onChange={e => setRefinePrompt(e.target.value)}
                    rows={2}
                    className="w-full px-2 py-1 border rounded text-sm mb-2"
                    placeholder="e.g., Add more color, include arrows between concepts, change the title..."
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={refineImage}
                      disabled={isRefiningImage || !refinePrompt.trim()}
                      className="px-4 py-1.5 bg-purple-600 text-white rounded text-sm font-medium hover:bg-purple-700 disabled:opacity-50"
                    >
                      {isRefiningImage ? 'Generating...' : 'Refine Image'}
                    </button>
                    <button
                      onClick={() => downloadImage(displayedImage.image_base64, displayedImage.image_mime)}
                      className="px-4 py-1.5 bg-green-600 text-white rounded text-sm font-medium hover:bg-green-700"
                    >
                      Download
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
    </div>
  )
}
